import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Row, Col, Spin } from "antd";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

function AddProduct() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(true);
  const { addToCart } = useCart();
  const [alert, setAlert] = useState({ type: "", message: "" });

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert({ type: "", message: "" }), 3000); // Auto-hide after 3s
  };

  useEffect(() => {
    const fetchProductDetail = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`/api/products/fetch/${slug}`);
        console.log("✅ API Response for product detail:", data);

        if (data.success) {
          const prod = data.data;
          console.log("🧩 Parsed product data:", prod);
          setProduct(prod);
          if (prod.colors?.length > 0) {
            console.log("🎨 Default selected color:", prod.colors[0]);
            setSelectedColor(prod.colors[0]);
          }
        } else {
          showAlert("error", "Failed to load product details.");
        }
      } catch (err) {
        console.error("❌ Error fetching product:", err);
        showAlert("error", "Something went wrong while loading product.");
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchProductDetail();
  }, [slug]);

  const getImageUrl = (imagePath) =>
    imagePath?.startsWith("/uploads") ? imagePath : imagePath || "";

  const handleAddToCart = () => {
    if (!product) {
      showAlert("error", "Product not found.");
      return;
    }

    if (!selectedColor) {
      showAlert("error", "Please select a color before adding to cart.");
      console.warn("⚠️ No color selected, aborting addToCart.");
      return;
    }

    console.log("🛍️ Attempting to add to cart...");
    console.log("➡️ Product:", product);
    console.log("🎨 Selected Color:", selectedColor);

    addToCart(product, 1, selectedColor.name);

    console.log("✅ Data sent to addToCart:", {
      productId: product._id,
      productTitle: product.title,
      color: selectedColor,
      quantity: 1,
    });

    showAlert("success", "Product successfully added to cart!");

    setTimeout(() => {
      navigate("/cart");
    }, 1500);
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Spin size="large" tip="Loading product..." />
      </div>
    );

  if (!product)
    return (
      <div className="text-center py-10 text-gray-600 font-custom text-lg">
        Product not found
      </div>
    );

  const activeImage =
    selectedColor?.images?.[0] ||
    product.colors?.[0]?.images?.[0] ||
    "/placeholder.png";

  return (
    <div className="main-production-dec pt-5 pb-10 my-5 relative">
      {/* ✅ Custom Alert */}
      {alert.message && (
        <div
          className={`fixed top-6 right-6 px-5 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 ${
            alert.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {alert.message}
        </div>
      )}

      <div className="container">
        <Row gutter={[32, 32]} align="middle" justify="center">
          {/* Left: Product Image */}
          <Col md={12}>
            <div className="flex flex-col items-center">
              <img
                key={activeImage}
                src={getImageUrl(activeImage)}
                alt={selectedColor?.name || "Product"}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(true)}
                className={`max-h-full object-contain transition-opacity duration-500 ease-in-out ${
                  imageLoaded ? "opacity-100" : "opacity-0"
                }`}
              />
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                  Loading...
                </div>
              )}
            </div>
          </Col>

          {/* Right: Product Info */}
          <Col md={12}>
            <div className="product-details">
              <h5 className="text-gray-400 mb-1 font-custom">
                {product.category?.name || "Category"}
              </h5>
              <h2 className="font-bold text-3xl mb-2 font-custom">
                {product.title}
              </h2>
              <p className="text-gray-500 font-custom">{product.description}</p>

              <div className="mt-5">
                <h4 className="font-semibold mb-2 font-custom">
                  Product Information
                </h4>
                <p className="text-sm text-gray-400 mb-2 font-custom">
                  SKU: {product.sku} | Tax: {product.tax}%
                </p>
                <p className="text-sm text-gray-500 mb-3 font-custom">
                  {product.subtitle}
                </p>

                <p className="text-sm text-gray-400 mb-3 font-custom">
                  {product.stock} in stock | ${product.price.toFixed(2)} each
                </p>

                {/* 🎨 Color Squares */}
                <div className="mt-4">
                  <h4 className="font-semibold mb-2 font-custom">
                    Available Colors
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {product.colors?.map((color) => (
                      <div
                        key={color._id}
                        onClick={() => {
                          console.log("🎯 Color clicked:", color);
                          setImageLoaded(false);
                          setSelectedColor(color);
                        }}
                        className={`w-10 h-10 rounded-md border-2 cursor-pointer transition-all duration-200 
                          ${
                            selectedColor?._id === color._id
                              ? "border-teal-500 ring-2 ring-teal-300 scale-105"
                              : "border-gray-300 hover:scale-105"
                          }`}
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleAddToCart}
                  className="relative font-custom text-lg font-semibold
                    overflow-hidden border-2 mt-7 border-teal-400 rounded-full
                    px-8 h-[46px] transition-all duration-300 ease-in-out
                    text-teal-600 bg-white hover:!bg-teal-500 hover:!border-black
                    hover:!text-white shadow-[6px_6px_0_0_#14b8a6]
                    hover:shadow-[0_0_0_0_#14b8a6]
                    active:translate-y-[3px] block"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default AddProduct;
